import React, { useState, useEffect, useRef } from 'react';
import '../view-archer-styles.css';

//material UI
import { Alert, Avatar, Box, Button, Card, CardHeader, CardContent, Chip, CircularProgress, Divider, Grid, Input, IconButton, Paper, Stack, Tabs, Tab, TextField, Typography, Tooltip } from '@mui/material/';
import { blue} from '@mui/material/colors';
import { Delete, Send } from '@mui/icons-material/';
import { ArrowUpward, ArrowDownward, Close, Edit} from '@mui/icons-material/';

//third parties:
import { Editor } from '@tinymce/tinymce-react';

//components import
import AuditNoteCard from './AuditNoteCard';
import FlaggedItemsTable, { excessContributionCategoryId } from './FlaggedItemsTable';
import ArcherLetter from '../dataObjects/ArcherLetter';
import NewArcherIssueDialog from './NewArcherIssueDialog';
import ArcherIssue from '../dataObjects/ArcherIssue';
import AlertInfoDialog from '../../../components/elements/AlertInfoDialog';
import AlertInfo from '../../../dataObjects/AlertInfo';
import AuditNote from '../dataObjects/AuditNote';
import EmailLog from '../../../dataObjects/EmailLog';
import EmailRecipient from '../dataObjects/EmailRecipient';
import NewLetterRecipientDialog from './NewLetterRecipientDialog';

interface LetterEditorProps {
    letterId: number,
    closeLetterEditor: () => void,
    openIssueEditor: (issue:any) => void,
}

interface TabPanelProps {
    children?: React.ReactNode;
    id: string;
    value: string;
}

function CustomTabPanel(props: TabPanelProps) {
    const { children, value, id, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== id}
            id={`letter-tabpanel-${id}`}
            aria-labelledby={`letter-tab-${id}`}
            {...other}
        >
            {value === id && (
                <Box sx={{ p: 3 }}>
                    <Grid>{children}</Grid>
                </Box>
            )}
        </div>
    );
}

function tabProps(id: string) {
    return {
        id: `letter-tab-${id}`,
        value: `${id}`,
        'aria-controls': `letter-tabpanel-${id}`,
    };
}

const ArcherLetterEditor = (props: LetterEditorProps) => {

    //initial state set up
    const [tabValue, setTabValue] = useState('preview');
    const [confirmActionData, setConfirmationAction] = useState({
        type: '',
        id: props.letterId
    })

    //text editor setup
    const editorAPIKey = process.env.REACT_APP_EDITOR_API_KEY;
    const editorRef = useRef<any>(null);

    //loading indicator
    const [isLoading, setLoadingIndicator] = useState('');

    useEffect(() => {
        getLetter();
    }, [])

    /*Navigation*/
    const switchTab = (event: React.SyntheticEvent, newValue: string) => {
        if (newValue === "preview") {
            getLetterPreview();
        }
        setTabValue(newValue);
    };

    const closeLetterEditor = () => {
        props.closeLetterEditor();
    }

    /*LETTER*/
    //populate letter
    const [letter, setLetter] = useState<ArcherLetter>();
    const [letterContent, setLetterContent] = useState('');
    const [letterSubject, setLetterSubject] = useState('');
    const [letterDate, setLetterDate] = useState('');
    const [letterPreviewContent, setPreivewContent] = useState('');
    const [sentLetterUrl, setSentLetterUrl] = useState('');

    const getLetter = async () => {

        let url = `/Archer3/GetLetter?letterId=${props.letterId}`;
        await fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: GetLetter',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    let letter = result.result;
                    setLoadingIndicator('hidden');
                    setLetter(letter);
                    setLetterContent(letter.body);
                    setLetterSubject(letter.subject);
                    setLetterDate(letter.date);
                    setSentLetterUrl(letter.fileUrl);
                    if (!letter.isSent) {
                        getLetterPreview();
                    }
                }
               
            })
    }

    const saveLetterSubject = (subject: string) => {
        var params = [
            `letterId=${props.letterId}`,
            `subject=${encodeURIComponent(subject)}`
        ].join('&');
        let url = `/Archer3/SaveLetterSubject?${params}`
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: SaveLetterSubject',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    setLetterSubject(subject);
                }
            })
    }

    const saveLetterDate = () => {
        var params = [
            `letterId=${props.letterId}`,
            `date=${letterDate}`
        ].join('&');
        let url = `/Archer3/SaveLetterDate?${params}`
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: SaveLetterDate',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
            })
    }

    const confirmSendLetter = (letterDate: string) => {
        let today = (new Date().getMonth() + 1) + '/' + new Date().getDate() + '/' + new Date().getFullYear();
        if (new Date(letterDate).getTime() !== new Date(today).getTime()) {
            let info = {
                type: 'confirm',
                title: "Confirm: Update to today's date",
                buttonLabel: "Yes",
                errorMessage: "This letter's date is not today, would you like to update its date to today?",
                secondaryErrorMessage: ''
            };

            let action = {
                type: 'sendLetter',
                id: props.letterId
            }

            setConfirmationAction(action);
            setAlertInfo(info);
            setAlertInfoDialog(true);
        }
        else {
            sendLetter(false, false);
        }
    }

    const sendLetter = (updateDate: boolean, toMe: boolean) => {
        
        var params = [
            `letterId=${props.letterId}`,
            `toMe=${toMe}`,
            `changeDateToToday=${updateDate}`,
            `overrideWarnings=${!updateDate}`
        ].join('&');
        let url = `/Archer3/SendLetter?${params}`
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: SendLetter',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {

                    let successMessage = toMe ? 'Email successfully sent to yourself!' : 'Email successfully sent to the filer!';

                    let info = {
                        type: 'success',
                        title: 'Email sent',
                        errorMessage: successMessage,
                        secondaryErrorMessage: ''
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                    getLetter();
                }
            })
    }

    const sendLetterToMe = () => {
        sendLetter(false, true);
    }

    //TODO
    const resendLetter = () => {
        
    }

    const confirmDeleteLetter = () => {
        let info = {
            type: 'warning',
            title: 'Warning: Delete Archer Letter',
            buttonLabel: "Delete",
            errorMessage: 'Are you sure you would like to delete this letter?',
            secondaryErrorMessage: ''
        };

        let action = {
            type: 'deleteLetter',
            id: props.letterId
        }

        setConfirmationAction(action);
        setAlertInfo(info);
        setAlertInfoDialog(true);
    }

    const deleteLetter = () => {
        let url = `/Archer3/DeleteLetter?letterId=${props.letterId}`
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: DeleteLetter',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    setConfirmationAction({ type: '', id: 0});
                    closeLetterEditor();
                }
            })
    }

    const saveLetterBody = () => {
        let letterBody = '';

        if (editorRef.current) {
            letterBody = editorRef.current.getContent();
        }

        fetch('/Archer3/SaveLetterBody', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(
                {
                    letterId: props.letterId,
                    body:letterBody
                })
        }).then(result => result.json())
          .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: SaveLetterBody',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    getLetter();
                }
            });  
    }

    const getLetterPreview = async () => {
        var params = [
            `letterId=${props.letterId}`,
        ].join('&');

        let url = `/Archer3/GetLetterPreview?${params}`;

        await fetch(url)
            .then(result => result.text())
            .then(result => {
                let html = result;
                setPreivewContent(html);
            })
    }

    const confirmRevertLetterToDraft = () => {
        let info = {
            type: 'warning',
            title: 'Warning: Revert Letter to Draft',
            buttonLabel: "Revert",
            errorMessage: 'Are you sure you would like to revert this letter to draft?',
            secondaryErrorMessage: ''
        };

        let action = {
            type: 'revertLetter',
            id: props.letterId
        }

        setConfirmationAction(action);
        setAlertInfo(info);
        setAlertInfoDialog(true);
    }

    const revertLetterToDraft = () => {
        let url = `/Archer3/RevertLetterToDraft?letterId=${props.letterId}`
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: RevertLetterToDraft',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    setConfirmationAction({ type: '', id: 0 });
                    props.closeLetterEditor();
                }
            })
    }

    const displayRecipientEmailEvents = (recipient: EmailRecipient) => {
        if (recipient.emailEvents.length===0) {
            return <Chip key={recipient.id} label={`${recipient.isPrimaryRecipient ? 'To' : 'Cc'}: ${recipient.firstName} ${recipient.lastName} (${recipient.emailAddress})`} variant="outlined" color="primary" />
        }

        if (recipient.emailEvents.filter(emailLog => emailLog.eventType === 'delivered').length>0) { 
            return <Chip key={recipient.id} label={`${recipient.isPrimaryRecipient ? 'To' : 'Cc'}: ${recipient.firstName} ${recipient.lastName} (${recipient.emailAddress}) - delivered`} variant="outlined" color="success" />
        }

        if (recipient.emailEvents.filter(emailLog => emailLog.eventType === 'failed').length > 0) {
            let event = recipient.emailEvents.filter(emailLog => emailLog.eventType === 'failed')[0];
            return (
                <Tooltip title={event.deliveryStatusMessage} placement="top">
                    <Chip key={recipient.id} label={`${recipient.isPrimaryRecipient ? 'To' : 'Cc'}: ${recipient.firstName} ${recipient.lastName} (${recipient.emailAddress}) - failed`} variant="outlined" color="error" />
                </Tooltip>
            )
        }
    }

    /*ISSUE LIST*/
    const openIssueEditor = (issue:any) => {
        props.openIssueEditor(issue);
    }

    const confirmDeleteIssue = (issueId: number) => {
        let info = {
            type: 'warning',
            title: 'Warning: Delete Archer Issue',
            buttonLabel: "Delete",
            errorMessage: 'Are you sure you would like to delete this issue from the letter?',
            secondaryErrorMessage: ''
        };

        let action = {
            type: 'deleteIssue',
            id: issueId
        }

        setConfirmationAction(action);
        setAlertInfo(info);
        setAlertInfoDialog(true);
    }

    const removeIssueFromLetter = (issueId:number) => {
        var params = [
            `letterId=${props.letterId}`,
            `issueId=${issueId}`
        ].join('&');
        let url = `/Archer3/RemoveIssueFromLetter?${params}`
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: RemoveIssueFromLetter',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    setAlertInfoDialog(false);
                    getLetter();
                }
            })
    }

    const promoteIssue = (issueId: number) => {
        var params = [
            `letterId=${props.letterId}`,
            `issueId=${issueId}`
        ].join('&');
        let url = `/Archer3/promoteIssue?${params}`
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: PromoteIssue',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    //maybe just remove it from UI?
                    getLetter();
                }
            })
    }

    const demoteIssue = (issueId: number) => {
        var params = [
            `letterId=${props.letterId}`,
            `issueId=${issueId}`
        ].join('&');
        let url = `/Archer3/demoteIssue?${params}`
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: DemoteIssue',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    //maybe just remove it from UI?
                    getLetter();
                }
            })
    }

    const removeFlaggedItem = (flaggedItemId: number) => {
        var params = [
            `flaggedItemId=${flaggedItemId}`,
        ].join('&');
        let url = `/Archer3/removeFlaggedItem?${params}`
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: RemoveFlaggedItem',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    getLetter();
                }
            })
    }

    const updateFlaggedItems = () => {
        getLetter();
    }

    const displayIssueListItem = (issue: ArcherIssue) => {
        let status = issue.archerStatus;
        if (status.indexOf(",") > 0) {
            status = status.substring(0, status.indexOf(","));
        }
        switch (status) {
            case 'Unresolved':
                return (
                    <Alert icon={false} severity="error"
                        onClick={() => openIssueEditor(issue)}
                        className="is-clickable"
                        action={
                            <IconButton aria-label="edit" onClick={() => openIssueEditor(issue)}>
                                <Edit className="small-icon" />
                            </IconButton>
                        }
                    >
                        <Chip label={status} variant="outlined" size="small" color="error" sx={{ marginRight: 1 }} />
                        {issue.date} {issue.issueTypeCode} {issue.header} ({issue.flaggedItems.length} items)
                    </Alert>
                );
            case 'Delinquent':
                return (
                    <Alert icon={false} severity="error"
                        onClick={() => openIssueEditor(issue)}
                        className="is-clickable"
                        action={
                            <IconButton aria-label="edit" onClick={() => openIssueEditor(issue)}>
                                <Edit className="small-icon" />
                            </IconButton>
                        }
                    >
                        <Chip label={status} variant="outlined" size="small" color="error" sx={{ marginRight: 1 }} />
                        {issue.date} {issue.issueTypeCode} {issue.header} ({issue.flaggedItems.length} items)
                    </Alert>
                );
            case 'Outstanding':
                return (
                    <Alert icon={false} severity="warning"
                        onClick={() => openIssueEditor(issue)}
                        className="is-clickable"
                        action={
                            <IconButton aria-label="edit" onClick={() => openIssueEditor(issue)}>
                                <Edit className="small-icon" />
                            </IconButton>
                        }
                    >
                        <Chip label={status} variant="outlined" size="small" color="error" sx={{ marginRight: 1 }} />
                        {issue.date} {issue.issueTypeCode} {issue.header} ({issue.flaggedItems.length} items)
                    </Alert>
                );
            case 'Held':
            case 'Punted':
                return (
                    <Alert icon={false} severity="info"
                        onClick={() => openIssueEditor(issue)}
                        className="is-clickable"
                        action={
                            <IconButton aria-label="edit" onClick={() => openIssueEditor(issue)}>
                                <Edit className="small-icon" />
                            </IconButton>
                        }
                    >
                        <Chip label={issue.archerStatus} variant="outlined" size="small" color="primary" sx={{ marginRight: 1 }} />
                        {issue.date} {issue.issueTypeCode} {issue.header} ({issue.flaggedItems.length} items)
                    </Alert>
                );
            case 'Referred':
                return (
                    <Alert icon={false} severity="info"
                        onClick={() => openIssueEditor(issue)}
                        className="is-clickable"
                        action={
                            <IconButton aria-label="edit" onClick={() => openIssueEditor(issue)}>
                                <Edit className="small-icon" />
                            </IconButton>
                        }
                    >
                        <Chip label={status} variant="outlined" size="small" color="primary" sx={{ marginRight: 1 }} />
                        {issue.date} {issue.issueTypeCode} {issue.header} ({issue.flaggedItems.length} items)
                    </Alert>
                );
            case 'Resolved':
                return (
                    <Alert icon={false} severity="success"
                        onClick={() => openIssueEditor(issue)}
                        className="is-clickable"
                        action={
                            <IconButton aria-label="edit" onClick={() => openIssueEditor(issue)}>
                                <Edit className="small-icon" />
                            </IconButton>
                        }
                    >
                        <Chip label={status} variant="outlined" size="small" color="success" sx={{ marginRight: 1 }} />
                        {issue.date} {issue.issueTypeCode} {issue.header} ({issue.flaggedItems.length} items)
                    </Alert>
                );
            default:
                return '';
        }
    }

    /*Add New Issue*/
    const [isNewIssuesDialogOpen, setNewIssueDialogOpen] = useState(false)
  
    const openNewIssueDialog = () => {
        setNewIssueDialogOpen(true);
    };

    const closeNewIssueDialog = (isNewIssueAdded: boolean) => {
        setNewIssueDialogOpen(false);
        if (isNewIssueAdded) {
            getLetter();
        }
    };

    /*NOTES*/
    const [letterNote, setLetterNote] = useState('');
    const [noteLabel, setNoteLabel] = useState('New Letter Note');
    const [noteId, setNoteId] = useState(0);
    const updateLetterNote = (event: any) => {
        setLetterNote(event.target.value);
    }

    const saveLetterNote = () => {
        var params = [
            `letterId=${props.letterId}`,
            `id=${noteId}`,
            `body=${encodeURIComponent(letterNote)}`
        ].join('&');
        let url = `/Archer3/SaveNote?${params}`
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: SaveNote',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    setLetterNote('');
                    getLetter();
                }
            })
    }

    const editLetterNote = (note: AuditNote) => {
        setNoteId(note.id);
        setNoteLabel('Edit Note...')
        setLetterNote(note.body);
    }

    const deleteNote = (noteId: number) => {
        let url = `/Archer3/DeleteNote?noteId=${noteId}`
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: DeleteNote',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    getLetter();
                }
            })
    }

    /*RECIPIENTS*/
    const removeLetterRecipient = (recipientId: number) => {
        let url = `/Archer3/removeLetterRecipient?recipientId=${recipientId}`
        fetch(url)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: SaveNote',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    getLetter();
                }
            })
    }

    const [isNewRecipientDialogOpen, setNewRecipientDialogOpen] = useState(false)

    const openNewRecipientDialog = () => {
        setNewRecipientDialogOpen(true);
    };

    const closeNewRecipientDialog = (isNewRecipientAdded: boolean) => {
        setNewRecipientDialogOpen(false);
        if (isNewRecipientAdded) {
            getLetter();
        }
    };

    /*Error Handling*/
    const [isAlertInfoDialogOpen, setAlertInfoDialog] = useState(false);
    const [alertInfo, setAlertInfo] = useState<AlertInfo>();
    const closeAlertInfoDialog = () => {
        setAlertInfoDialog(false);
    }

    const confirmAction = (confirm: boolean) => {
        let type = confirmActionData.type;
        let id = confirmActionData.id;
        if (confirm) {
            switch (type) {
                case 'deleteLetter':
                    deleteLetter();
                    break;
                case 'deleteIssue':
                    removeIssueFromLetter(id);
                    break;
                case 'revertLetter':
                    revertLetterToDraft();
                    break;
                case 'sendLetter':
                    sendLetter(true, false);
                    break;
            }
        }
        else {
            switch (type) {
                case 'sendLetter':
                    // not update to today's date, not to me
                    sendLetter(false, false);
                    break;
            }
        }
    }

    return (
        <div className="letter-editor-container">
            {letter && 
                <Grid container>
                {/*Editor Container*/}
                    <Grid item xs={9.5}>
                        {/*Draft Letter Container*/}
                        {
                            letter.letterStatus === "Draft" &&
                            <Box sx={{ width: '100%' }}>
                                    <Grid container className="letter-section">
                                        <Grid item xs={6}>
                                            <Chip className="is-clickable" label='Add New Issue' variant="outlined" color="success" onClick={openNewIssueDialog} />
                                        </Grid>
                                        <Grid item xs={6} justifyContent="flex-end" sx={{ display: 'flex' }}>
                                            <Stack direction="row" spacing={2}>
                                                <Button variant="outlined" color="success" size="small" className="small-action-button" endIcon={<Send />} onClick={()=>confirmSendLetter(letterDate)} >Send</Button>
                                                <Button variant="outlined" color="primary" size="small" className="small-action-button" onClick={sendLetterToMe} >Email Me</Button>
                                                <Button variant="outlined" color="error" size="small" className="small-action-button" startIcon={<Delete />} onClick={confirmDeleteLetter}>Delete</Button>
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                    <Grid container className="letter-section" sx={{ marginTop: -0.5 }}>
                                        <Grid item xs={8}>
                                        <Stack direction="row" spacing={2}>
                                            <Typography variant="subtitle1" sx={{ color: blue[800] }}>Subject</Typography>
                                            <Input value={letterSubject} sx={{ width: '80%' }} onChange={(event) => { saveLetterSubject(event.target.value) }} />
                                          </Stack>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Stack direction="row" spacing={2}>
                                                <Typography variant="subtitle1" sx={{ color: blue[800] }}>Letter Date</Typography>
                                                <Input value={letterDate} onChange={(event) => { setLetterDate(event.target.value) }} onMouseLeave={saveLetterDate} />
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                    <Grid container className="letter-section">
                                        <Grid item xs={12}>
                                            <Stack direction="row" spacing={2} alignItems="center">
                                            <Typography variant="subtitle1" sx={{ color: blue[800] }}>Recipients</Typography>
                                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                                    {
                                                        letter.recipients.map((recipient) =>
                                                            <Chip size="small" onDelete={(event) => { removeLetterRecipient(recipient.id) }} key={recipient.id} label={`${recipient.isPrimaryRecipient ? 'To' : 'Cc'}: ${recipient.firstName} ${recipient.lastName} (${recipient.emailAddress})`} variant="outlined" color="primary" />
                                                        )

                                                    }
                                                    <Chip label='Update Primary Recipient' variant="outlined" color="success" size="small" onClick={openNewRecipientDialog}></Chip>
                                                </Stack>
                                        </Stack>
                                        </Grid>
                                    </Grid>
                                <Grid container className="letter-section">
                                    <Grid item xs={12}>
                                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                            <Tabs value={tabValue} onChange={switchTab}>
                                                <Tab label="Issues" {...tabProps('issues')} />
                                                <Tab label="Body" {...tabProps('body')} />
                                                <Tab label="Preview" {...tabProps('preview')} />
                                            </Tabs>
                                        </Box>
                                        <CustomTabPanel value={tabValue} id={'issues'}>
                                            {/*Issue List*/}
                                            <Grid container className="issue-list-container">
                                                {
                                                    letter.issues.length > 0 &&
                                                    letter.issues.map((issue) =>
                                                        <Grid item xs={12} key={issue.issueId} sx={{ marginBottom: 2 }}>
                                                            <Card variant="outlined" className="issue-card" >
                                                                <CardHeader
                                                                    className="card-header"
                                                                    title={<span className={`issue-title ${issue.status}`} title={"issue-" + issue.issueId}>
                                                                        {issue.issueTypeCode} {issue.header} ({issue.flaggedItems.length} items)
                                                                    </span>}
                                                                    action={
                                                                        <Stack direction="row" spacing={0} alignItems="center">
                                                                            <IconButton aria-label="edit" onClick={() => openIssueEditor(issue)}>
                                                                                <Edit className="small-icon" />
                                                                            </IconButton>
                                                                            <IconButton aria-label="up" onClick={() => promoteIssue(issue.issueId)}>
                                                                                <ArrowUpward className="small-icon" />
                                                                            </IconButton>
                                                                            <IconButton aria-label="down" onClick={() => demoteIssue(issue.issueId)}>
                                                                                <ArrowDownward className="small-icon" />
                                                                            </IconButton>
                                                                            <IconButton aria-label="delete" onClick={() => confirmDeleteIssue(issue.issueId)}>
                                                                                <Close className="small-icon" />
                                                                            </IconButton>
                                                                        </Stack>
                                                                    }
                                                                />
                                                                <Divider light />
                                                                <CardContent className="card-content">
                                                                    {
                                                                        issue.flaggedItems.length > 0 &&
                                                                        <Grid container>
                                                                                <FlaggedItemsTable flagItems={issue.flaggedItems} removeFlaggedItem={removeFlaggedItem} updateFlaggedItems={updateFlaggedItems} isGroupUiEditable={issue.gatewayIssueCategoryId === excessContributionCategoryId} cpfId={issue.cpfId} auditPeriodId={issue.auditPeriodId}></FlaggedItemsTable>
                                                                        </Grid>
                                                                    }
                                                                </CardContent>
                                                            </Card>
                                                        </Grid>
                                                    )
                                                }
                                            </Grid>
                                        </CustomTabPanel>
                                        <CustomTabPanel value={tabValue} id={'body'}>
                                            <Grid container className="text-editor-container">
                                                <Grid item xs={12}>
                                                    <Editor
                                                        apiKey={editorAPIKey}
                                                        onInit={(evt:any, editor:any) => editorRef.current = editor}
                                                        initialValue={letterContent}
                                                        init={{
                                                            height: 650,
                                                            menubar: true,
                                                            branding: false,
                                                            plugins: [
                                                                'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                                                                'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                                                                'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount', 'tinymcespellchecker'
                                                            ],
                                                            toolbar: 'undo redo | blocks | ' +
                                                                'bold italic underline forecolor fontsize | alignleft aligncenter ' +
                                                                'alignright alignjustify spellchecker | bullist numlist outdent indent | ' +
                                                                'removeformat | help | spellchecker',
                                                            content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                                                            fontsize_formats: "8pt 10pt 12pt 14pt 18pt 24pt 36pt"
                                                        }}
                                                        onMouseLeave={saveLetterBody}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </CustomTabPanel>
                                        <CustomTabPanel value={tabValue} id={'preview'}>
                                            <Box
                                                component="section"
                                                className="letter-preview"
                                                sx={{
                                                    p: 2,
                                                    border: '1px dashed grey'
                                                    }}
                                                >
                                                    <Grid container className="letter-preview-content" dangerouslySetInnerHTML={{ __html: letterPreviewContent }}></Grid>
                                            </Box>
                                        </CustomTabPanel>
                                    </Grid>
                                </Grid>
                            </Box>
                        }
                        {/*Sent Letter Container*/}
                        {
                            letter.letterStatus === "Sent"  &&
                            <Box sx={{ width: '100%' }}>
                                <Grid container className="letter-section">
                                    <Grid item xs={7} justifyContent="flex-start">
                                            {
                                                letter.recipients && letter.recipients.length === 0 &&
                                                <Stack direction="row" spacing={2}>
                                                    <Chip label='Delivered - delivery info is in Archer 2' color="success" variant="outlined" />
                                                </Stack>
                                            }
                                            {
                                                letter.recipients && letter.recipients.length > 0 && letter.recipients[0].emailEvents && letter.recipients[0].emailEvents.length>0 &&
                                                <Stack direction="row" spacing={2}>
                                                    <Chip label='Delivered' color="success" />
                                                </Stack>
                                            }
                                    </Grid>
                                    <Grid item xs={5} justifyContent="flex-end">
                                        <Stack direction="row" spacing={2}>
                                                <Button variant="outlined" color="primary" size="small" className="small-action-button" onClick={sendLetterToMe}>Email Me</Button>
                                                <Button variant="outlined" color="warning" size="small" className="small-action-button" onClick={confirmRevertLetterToDraft}>Revert Letter</Button>
                                        </Stack>
                                    </Grid>
                                </Grid>
                                <Grid container className="letter-section">
                                    <Grid item xs={6}>
                                        <Stack direction="row" spacing={2}>
                                            <Typography variant="subtitle1" sx={{ color: blue[800] }}>Subject</Typography>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold'}}>{letter.subject}</Typography>
                                        </Stack>
                                    </Grid>
                                </Grid>
                                <Grid container className="letter-section">
                                    <Grid item xs={12}>
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Typography variant="subtitle1" sx={{ color: blue[800] }}>Recipients</Typography>
                                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                                    {
                                                        letter.recipients.map((recipient) =>
                                                            displayRecipientEmailEvents(recipient)
                                                        )
                                                    }
                                                </Stack>
                                        </Stack>
                                    </Grid>
                                </Grid>
                                <Grid container className="letter-section">
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle1" sx={{ color: blue[800] }}>Issues</Typography>
                                        </Grid>
                                        <Grid item xs={1}></Grid>
                                        <Grid item xs={10}>
                                            <Stack spacing={1}>
                                                {
                                                    letter.issues.map((issue) => displayIssueListItem(issue)
                                                   )
                                                }
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                    <Grid container className="letter-section">
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle1" sx={{ color: blue[800] }}>Content</Typography>
                                        </Grid>
                                        <Grid item xs={1}></Grid>
                                        <Grid item xs={11}>
                                            <Grid container className="letter-preview">
                                                <iframe className="sent-letter-content" src={sentLetterUrl}></iframe>
                                            </Grid>
                                        </Grid>
                                </Grid>
                            </Box>
                        }
                        
                </Grid>
                {/*Letter Notes Container*/}
                <Grid item xs={0.5}></Grid>
                <Grid item xs={2} className="notes-container">
                    <Stack
                        direction="row" spacing={2}
                        justifyContent="flex-end"
                        alignItems="center"
                     >
                        <Avatar className="small-avatar is-clickable" onClick={closeLetterEditor}>
                            <Close className="small-icon" />
                        </Avatar>
                    </Stack>
                    {/*Notes List*/}
                    <Grid container sx={{ marginTop: 1 }}>
                        <Paper
                            component="form"
                            sx={{ p: 2 }}
                            >
                                <Stack
                                    direction="column"
                                    spacing={1}
                                    useFlexGap
                                    flexWrap="wrap"
                                    sx={{
                                        justifyContent: "center",
                                        alignItems: "flex-end",
                                    }}>
                                    <TextField
                                        label={noteLabel}
                                        size="small"
                                        onChange={updateLetterNote}
                                        value={letterNote}
                                        focused
                                        multiline
                                        rows={2}
                                        maxRows={10}
                                    />
                                    <Chip label='save' size="small" variant="outlined" color="success" onClick={saveLetterNote} />
                                </Stack>      
                        </Paper>
                    </Grid>
                    <Grid container spacing={2} sx={{ marginTop: 1 }}>
                        {
                            letter.notes.length > 0 &&
                                letter.notes.map((note) =>
                                    <AuditNoteCard key={note.id} note={note} deleteNote={deleteNote} saveNote={()=>editLetterNote(note)}></AuditNoteCard>
                                )
                        }      
                    </Grid>
                    </Grid>
                </Grid>
            }
            {/* add issue */}
            <NewArcherIssueDialog isNewIssueDialogOpen={isNewIssuesDialogOpen} letterId={props.letterId} closeNewIssueDialog={closeNewIssueDialog} />
            {
                alertInfo &&
                <AlertInfoDialog isAlertInfoDialogOpen={isAlertInfoDialogOpen} alertInfo={alertInfo} closeAlertInfoDialog={closeAlertInfoDialog} confirmAction={confirmAction}></AlertInfoDialog>
            }
            {/* add new recipient */}
            <NewLetterRecipientDialog isNewRecipientDialogOpen={isNewRecipientDialogOpen} letterId={props.letterId} closeNewRecipientDialog={closeNewRecipientDialog} />
            {/*loading indicator*/}
            <CircularProgress
                size={50}
                className={isLoading}
                sx={{
                    position: "fixed",
                    left: "50%",
                    top: "40%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 2
                }}
            />
        </div>
    )
}

export default ArcherLetterEditor